<!-- src/apps/secret/views/FileShareView.vue -->
<!-- secrets-files fork: Vue view for uploading an E2E-encrypted file. -->

<script setup lang="ts">
  import { computed, ref } from 'vue'
  import { useFileUploader } from '@/shared/composables/useFileUploader'
  import { useProductIdentity } from '@/shared/stores/identityStore'
  import SecretModeTabs from '@/apps/secret/components/layout/SecretModeTabs.vue'
  import BrandedHero from '@/apps/secret/components/layout/BrandedHero.vue'

  const { primaryColor, cornerClass, buttonTextLight } = useProductIdentity()
  const { upload, uploading, error } = useFileUploader()
  const file = ref<File | null>(null)
  const dragOver = ref(false)
  const result = ref<{ shareUrl: string; expiresAt: number | null } | null>(null)
  const ttlHours = ref(24)
  const copied = ref(false)

  const buttonTextClass = computed(() => (buttonTextLight ? 'text-white' : 'text-gray-800'))

  const fileMeta = computed(() => {
    if (!file.value) return null
    const sizeKB = (file.value.size / 1024).toFixed(1)
    return `${file.value.name} — ${sizeKB} KB`
  })

  function onDrop(ev: DragEvent) {
    ev.preventDefault()
    dragOver.value = false
    if (ev.dataTransfer?.files?.length) {
      file.value = ev.dataTransfer.files[0]
    }
  }

  function onPick(ev: Event) {
    const input = ev.target as HTMLInputElement
    if (input.files?.length) file.value = input.files[0]
  }

  async function onSubmit() {
    if (!file.value) return
    result.value = null
    try {
      const out = await upload(file.value, { ttlSeconds: ttlHours.value * 3600 })
      result.value = { shareUrl: out.shareUrl, expiresAt: out.expiresAt }
    } catch {
      // error is captured in the composable ref
    }
  }

  function copyShareUrl() {
    if (!result.value) return
    navigator.clipboard.writeText(result.value.shareUrl)
    copied.value = true
    window.setTimeout(() => (copied.value = false), 2000)
  }
</script>

<template>
  <div class="relative mx-auto w-full max-w-2xl px-4">
    <!-- Barra superior: Texto / Archivos -->
    <SecretModeTabs />

    <!-- Logo / wordmark + título -->
    <BrandedHero
      title="Compartir archivo cifrado"
      subtitle="El archivo se cifra en tu navegador con AES-GCM-256 y solo se descifra al abrirse una vez. El servidor nunca ve el contenido." />

    <!-- Tarjeta principal (mismos estilos que SecretForm) -->
    <div
      :class="[cornerClass]"
      class="overflow-visible border border-gray-200/50
        bg-gradient-to-br from-white to-gray-50/30
        shadow-[0_8px_30px_rgb(0,0,0,0.12),0_2px_8px_rgb(0,0,0,0.08)]
        backdrop-blur-sm
        dark:border-gray-700/50 dark:from-slate-900 dark:to-slate-800/30
        dark:shadow-[0_8px_30px_rgb(0,0,0,0.4),0_2px_8px_rgb(0,0,0,0.3)]">
      <!-- Contenido -->
      <div class="p-8">
        <!-- Dropzone -->
        <div
          :class="[
            cornerClass,
            dragOver ? '' : 'border-gray-300/70 dark:border-gray-600/70',
          ]"
          class="border-2 border-dashed bg-white/40 p-8 text-center transition-colors dark:bg-slate-800/40"
          :style="dragOver ? { borderColor: primaryColor } : undefined"
          @dragover.prevent="dragOver = true"
          @dragleave="dragOver = false"
          @drop="onDrop">
          <p class="mb-1 text-gray-700 dark:text-gray-200">Arrastra un archivo o</p>
          <label class="cursor-pointer font-medium underline" :style="{ color: primaryColor }">
            elige uno
            <input type="file" class="hidden" @change="onPick" />
          </label>
          <p v-if="fileMeta" class="mt-3 font-mono text-sm text-gray-600 dark:text-gray-300">
            {{ fileMeta }}
          </p>
        </div>

        <!-- TTL -->
        <div class="mt-6 flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
          <label for="ttl" class="font-brand">Caduca en (horas):</label>
          <input
            id="ttl"
            v-model.number="ttlHours"
            type="number"
            min="1"
            max="720"
            :class="[cornerClass]"
            class="w-24 border border-gray-200/60 bg-white/80 px-3 py-2 text-sm text-gray-900 backdrop-blur-sm transition-all duration-300 hover:border-gray-300/80 focus:border-blue-500/80 focus:bg-white focus:outline-none focus:ring-4 focus:ring-blue-500/20 dark:border-gray-700/60 dark:bg-slate-800/80 dark:text-white" />
        </div>

        <p v-if="error" class="mt-4 text-sm text-red-600 dark:text-red-400">{{ error }}</p>
      </div>

      <!-- Pie con botón de acción (mismo patrón que SecretForm) -->
      <div class="border-t border-gray-200/50 dark:border-gray-700/50">
        <div class="p-8">
          <div class="flex justify-end">
            <button
              type="button"
              :class="[cornerClass, buttonTextClass]"
              class="group relative flex w-full items-center justify-center gap-2.5 overflow-hidden px-5 py-3.5 text-lg font-semibold transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-offset-2 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto dark:focus:ring-offset-slate-900"
              :style="{
                backgroundColor: primaryColor,
                '--tw-ring-color': primaryColor,
                '--tw-ring-opacity': '0.2',
              }"
              :disabled="!file || uploading"
              @click="onSubmit">
              <span
                class="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                aria-hidden="true"></span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                class="relative z-10 size-5">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
              <span class="relative z-10">
                {{ uploading ? 'Cifrando y subiendo…' : 'Cifrar y subir' }}
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Resultado -->
    <div
      v-if="result"
      :class="[cornerClass]"
      class="mt-6 border border-gray-200/50 bg-gradient-to-br from-white to-gray-50/30 p-8 shadow-[0_8px_30px_rgb(0,0,0,0.12),0_2px_8px_rgb(0,0,0,0.08)] backdrop-blur-sm dark:border-gray-700/50 dark:from-slate-900 dark:to-slate-800/30">
      <p class="font-semibold text-gray-900 dark:text-white">Enlace de un solo uso</p>
      <code
        :class="[cornerClass]"
        class="mt-2 block break-all bg-gray-50 p-3 text-xs text-gray-800 dark:bg-gray-900 dark:text-gray-200">
        {{ result.shareUrl }}
      </code>
      <button
        class="mt-3 text-sm font-medium underline"
        :style="{ color: primaryColor }"
        @click="copyShareUrl">
        {{ copied ? '¡Copiado!' : 'Copiar' }}
      </button>
      <p v-if="result.expiresAt" class="mt-2 text-xs text-gray-500 dark:text-gray-400">
        Caduca: {{ new Date(result.expiresAt * 1000).toLocaleString() }}
      </p>
      <p class="mt-3 text-xs text-amber-700 dark:text-amber-500">
        ⚠ Envía este enlace al receptor por un canal seguro. El fragmento
        (la parte después del #) es la passphrase y nunca llega al servidor.
      </p>
    </div>
  </div>
</template>
